import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Card, 
    Form, 
    Input, 
    Select, 
    DatePicker, 
    InputNumber, 
    Button, 
    Row, 
    Col, 
    message,
    Divider,
    Alert,
    Space,
    Typography,
    Tooltip
} from 'antd';
import { 
    ArrowLeftOutlined, 
    SaveOutlined, 
    EyeOutlined,
    PercentageOutlined,
    DollarOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    ShopOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { CouponType, CreateCouponDto } from '../../../../types/coupon.types';
import { Company, CompanyStatus } from '../../../../types/company.types';
import couponService from '../../../../services/coupon/coupon.service';
import { productService } from '../../../../services/product/product.service';
import companyService from '../../../../services/company/company.service';
import { all_routes } from '../../../../router/all_routes';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface Product {
    id: number;
    name: string;
    durationMonths: number;
    pricePerUserPerMonth: number;
    isActive: boolean;
    description?: string;
    createdAt: string;
    updatedAt: string;
}

const CreateCouponPage: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [couponType, setCouponType] = useState<CouponType>(CouponType.PERCENTAGE);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    useEffect(() => {
        fetchProducts();
        fetchCompanies();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const productList = await productService.getAllProducts();
            setProducts(productList.filter(p => p.isActive));
        } catch (error) {
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const companyList = await companyService.getAllCompanies();
            setCompanies(companyList.filter(c => c.status === CompanyStatus.ACTIVE));
        } catch (error) {
        } finally {
            setLoadingCompanies(false);
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            
            const dto: CreateCouponDto = {
                code: values.code.toUpperCase(),
                name: values.name,
                description: values.description,
                type: values.type,
                value: values.value,
                usageLimit: values.usageLimit || 0,
                companyUsageLimit: values.companyUsageLimit || 1,
                startDate: values.dateRange[0].format('YYYY-MM-DD'),
                endDate: values.dateRange[1].format('YYYY-MM-DD'),
                minUserCount: values.minUserCount || undefined,
                applicableProductIds: values.applicableProductIds || undefined,
                specificCompanyIds: values.specificCompanyIds || undefined,
            };

            await couponService.createCoupon(dto);
            message.success('Kupon başarıyla oluşturuldu!');
            navigate(all_routes.coupons);
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Kupon oluşturulurken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const generateRandomCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        form.setFieldsValue({ code: result });
    };

    const watchedValues = Form.useWatch([], form);

    const getDiscountPreview = () => {
        if (!watchedValues?.value) return '';
        
        const sampleAmount = 200;
        if (couponType === CouponType.PERCENTAGE) {
            const discount = (sampleAmount * watchedValues.value) / 100;
            return `Örnek: ₺${sampleAmount} için ₺${discount} indirim (₺${sampleAmount - discount} ödeme)`;
        } else {
            const discount = Math.min(watchedValues.value, sampleAmount);
            return `Örnek: ₺${sampleAmount} için ₺${discount} indirim (₺${sampleAmount - discount} ödeme)`;
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <Button 
                            type="text" 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate(all_routes.coupons)}
                            className="me-2"
                        />
                        <div>
                            <h4 className="page-title mb-1">
                                <i className="ti ti-ticket me-2 text-primary"></i>
                                Yeni Kupon Oluştur
                            </h4>
                            <p className="text-muted mb-0">İndirim kuponu oluşturun ve yönetin</p>
                        </div>
                    </div>
                    <Space>
                        <Button 
                            icon={<EyeOutlined />}
                            onClick={() => setPreviewVisible(!previewVisible)}
                        >
                            Önizleme
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<SaveOutlined />}
                            loading={loading}
                            onClick={() => form.submit()}
                        >
                            Kuponu Kaydet
                        </Button>
                    </Space>
                </div>

                <Row gutter={24}>
                    {/* Form */}
                    <Col xs={24} lg={previewVisible ? 16 : 24}>
                        <Card className="border-0 shadow-sm">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSubmit}
                                initialValues={{
                                    type: CouponType.PERCENTAGE,
                                    value: 10,
                                    usageLimit: 0,
                                    companyUsageLimit: 1,
                                }}
                            >
                                {/* Temel Bilgiler */}
                                <Title level={5} className="text-primary mb-3">
                                    <i className="ti ti-info-circle me-2"></i>
                                    Temel Bilgiler
                                </Title>

                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Kupon Kodu"
                                            name="code"
                                            rules={[
                                                { required: true, message: 'Kupon kodu gerekli' },
                                                { min: 3, message: 'En az 3 karakter olmalı' },
                                                { max: 20, message: 'En fazla 20 karakter olmalı' },
                                                { pattern: /^[A-Z0-9]+$/, message: 'Sadece büyük harf ve rakam kullanın' }
                                            ]}
                                        >
                                            <Input 
                                                placeholder="PREMIUM20"
                                                suffix={
                                                    <Button 
                                                        type="link" 
                                                        size="small"
                                                        onClick={generateRandomCode}
                                                        className="p-0"
                                                    >
                                                        Rastgele
                                                    </Button>
                                                }
                                                style={{ textTransform: 'uppercase' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Kupon Adı"
                                            name="name"
                                            rules={[
                                                { required: true, message: 'Kupon adı gerekli' },
                                                { max: 100, message: 'En fazla 100 karakter olmalı' }
                                            ]}
                                        >
                                            <Input placeholder="Premium İndirim Kuponu" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Açıklama"
                                    name="description"
                                >
                                    <TextArea 
                                        placeholder="Kupon hakkında açıklama..."
                                        rows={3}
                                        maxLength={500}
                                        showCount
                                    />
                                </Form.Item>

                                <Divider />

                                {/* İndirim Ayarları */}
                                <Title level={5} className="text-primary mb-3">
                                    <i className="ti ti-percentage me-2"></i>
                                    İndirim Ayarları
                                </Title>

                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="İndirim Tipi"
                                            name="type"
                                            rules={[{ required: true, message: 'İndirim tipi seçin' }]}
                                        >
                                            <Select 
                                                onChange={(value) => setCouponType(value)}
                                                size="large"
                                            >
                                                <Option value={CouponType.PERCENTAGE}>
                                                    <Space>
                                                        <PercentageOutlined />
                                                        Yüzde İndirim
                                                    </Space>
                                                </Option>
                                                <Option value={CouponType.FIXED}>
                                                    <Space>
                                                        <DollarOutlined />
                                                        Sabit Tutar İndirim
                                                    </Space>
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label={couponType === CouponType.PERCENTAGE ? 'İndirim Yüzdesi' : 'İndirim Tutarı (₺)'}
                                            name="value"
                                            rules={[
                                                { required: true, message: 'İndirim değeri gerekli' },
                                                { type: 'number', min: 1, message: 'En az 1 olmalı' },
                                                ...(couponType === CouponType.PERCENTAGE 
                                                    ? [{ type: 'number' as const, max: 100, message: 'En fazla 100 olabilir' }]
                                                    : [{ type: 'number' as const, max: 10000, message: 'En fazla 10.000₺ olabilir' }]
                                                )
                                            ]}
                                        >
                                            <InputNumber
                                                className="w-100"
                                                size="large"
                                                min={1}
                                                max={couponType === CouponType.PERCENTAGE ? 100 : 10000}
                                                suffix={couponType === CouponType.PERCENTAGE ? '%' : '₺'}
                                                placeholder={couponType === CouponType.PERCENTAGE ? '20' : '50'}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <div className="mt-4 p-3 bg-light rounded">
                                            <Text type="secondary" className="small">
                                                {getDiscountPreview()}
                                            </Text>
                                        </div>
                                    </Col>
                                </Row>

                                <Divider />

                                {/* Kullanım Ayarları */}
                                <Title level={5} className="text-primary mb-3">
                                    <i className="ti ti-users me-2"></i>
                                    Kullanım Ayarları
                                </Title>

                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label={
                                                <Space>
                                                    Toplam Kullanım Limiti
                                                    <Tooltip title="0 = Sınırsız kullanım">
                                                        <InfoCircleOutlined />
                                                    </Tooltip>
                                                </Space>
                                            }
                                            name="usageLimit"
                                        >
                                            <InputNumber
                                                className="w-100"
                                                size="large"
                                                min={0}
                                                max={10000}
                                                placeholder="100 (0 = Sınırsız)"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Şirket Başına Kullanım Limiti"
                                            name="companyUsageLimit"
                                            rules={[
                                                { required: true, message: 'Şirket limiti gerekli' },
                                                { type: 'number', min: 1, message: 'En az 1 olmalı' }
                                            ]}
                                        >
                                            <InputNumber
                                                className="w-100"
                                                size="large"
                                                min={1}
                                                max={10}
                                                placeholder="1"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label={
                                                <Space>
                                                    Minimum Kullanıcı Sayısı
                                                    <Tooltip title="Bu kuponun kullanılabilmesi için şirketin sahip olması gereken minimum kullanıcı sayısı">
                                                        <InfoCircleOutlined />
                                                    </Tooltip>
                                                </Space>
                                            }
                                            name="minUserCount"
                                        >
                                            <InputNumber
                                                className="w-100"
                                                size="large"
                                                min={1}
                                                max={1000}
                                                placeholder="Minimum kullanıcı sayısı"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>



                                <Divider />

                                {/* Ürün ve Şirket Kısıtlamaları */}
                                <Title level={5} className="text-primary mb-3">
                                    <i className="ti ti-filter me-2"></i>
                                    Ürün ve Şirket Kısıtlamaları
                                </Title>

                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label={
                                                <Space>
                                                    <ShopOutlined />
                                                    Geçerli Ürünler
                                                    <Tooltip title="Kuponun hangi ürünlerde kullanılabileceğini belirler. Boş bırakılırsa tüm ürünlerde geçerli olur">
                                                        <InfoCircleOutlined />
                                                    </Tooltip>
                                                </Space>
                                            }
                                            name="applicableProductIds"
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Ürün seçin (Boş = Tüm ürünler)"
                                                loading={loadingProducts}
                                                size="large"
                                                allowClear
                                                showSearch
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {products.map(product => (
                                                    <Option key={product.id} value={product.id}>
                                                        {product.name} ({product.durationMonths} ay - ₺{product.pricePerUserPerMonth}/kullanıcı)
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label={
                                                <Space>
                                                    <TeamOutlined />
                                                    Belirli Şirketler
                                                    <Tooltip title="Kuponun sadece belirli şirketler tarafından kullanılabilmesini sağlar. Boş bırakılırsa tüm şirketler kullanabilir">
                                                        <InfoCircleOutlined />
                                                    </Tooltip>
                                                </Space>
                                            }
                                            name="specificCompanyIds"
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Şirket seçin (Boş = Tüm şirketler)"
                                                loading={loadingCompanies}
                                                size="large"
                                                allowClear
                                                showSearch
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {companies.map(company => (
                                                    <Option key={company.id} value={company.id}>
                                                        {company.name} ({company.email})
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />

                                {/* Geçerlilik Tarihleri */}
                                <Title level={5} className="text-primary mb-3">
                                    <CalendarOutlined className="me-2" />
                                    Geçerlilik Tarihleri
                                </Title>

                                <Form.Item
                                    label="Geçerlilik Tarihi Aralığı"
                                    name="dateRange"
                                    rules={[{ required: true, message: 'Geçerlilik tarihleri gerekli' }]}
                                >
                                    <DatePicker.RangePicker
                                        className="w-100"
                                        size="large"
                                        format="DD.MM.YYYY"
                                        placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                                    />
                                </Form.Item>

                                <Alert
                                    message="Kupon Oluşturma Bilgilendirmesi"
                                    description="Kupon oluşturulduktan sonra kod değiştirilemez. Diğer tüm ayarlar düzenlenebilir. Kupon otomatik olarak aktif durumda oluşturulacaktır."
                                    type="info"
                                    showIcon
                                    className="mt-4"
                                />
                            </Form>
                        </Card>
                    </Col>

                    {/* Önizleme */}
                    {previewVisible && (
                        <Col xs={24} lg={8}>
                            <Card className="border-0 shadow-sm position-sticky" style={{ top: '20px' }}>
                                <Title level={5} className="text-center mb-3">
                                    <EyeOutlined className="me-2" />
                                    Kupon Önizlemesi
                                </Title>
                                
                                <div className="text-center p-4 border rounded bg-gradient" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white'
                                }}>
                                    <div className="fs-4 fw-bold mb-2">
                                        {watchedValues?.code || 'KUPON_KODU'}
                                    </div>
                                    <div className="fs-6 mb-3">
                                        {watchedValues?.name || 'Kupon Adı'}
                                    </div>
                                    <div className="fs-1 fw-bold mb-2">
                                        {watchedValues?.type === CouponType.PERCENTAGE 
                                            ? `%${watchedValues?.value || 0}` 
                                            : `₺${watchedValues?.value || 0}`
                                        }
                                    </div>
                                    <div className="small opacity-75">
                                        {watchedValues?.type === CouponType.PERCENTAGE ? 'İndirim' : 'Sabit İndirim'}
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <div className="d-flex justify-content-between py-2 border-bottom">
                                        <Text type="secondary">Kullanım Limiti:</Text>
                                        <Text strong>
                                            {watchedValues?.usageLimit === 0 ? 'Sınırsız' : watchedValues?.usageLimit || 0}
                                        </Text>
                                    </div>
                                    <div className="d-flex justify-content-between py-2 border-bottom">
                                        <Text type="secondary">Şirket Limiti:</Text>
                                        <Text strong>{watchedValues?.companyUsageLimit || 1}</Text>
                                    </div>
                                    <div className="d-flex justify-content-between py-2 border-bottom">
                                        <Text type="secondary">İlk Kez Premium:</Text>
                                        <Text strong>{watchedValues?.isFirstTimeOnly ? 'Evet' : 'Hayır'}</Text>
                                    </div>
                                    {watchedValues?.dateRange && (
                                        <div className="d-flex justify-content-between py-2">
                                            <Text type="secondary">Geçerlilik:</Text>
                                            <Text strong className="text-end">
                                                {dayjs(watchedValues.dateRange[0]).format('DD.MM.YYYY')} - 
                                                {dayjs(watchedValues.dateRange[1]).format('DD.MM.YYYY')}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>
        </div>
    );
};

export default CreateCouponPage;
